import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  FileEdit, 
  FolderOpen, 
  Users, 
  BarChart3, 
  Settings,
  Radar,
  Rocket,
  Building2,
  Users2,
  ClipboardCheck,
  Presentation,
  X,
  Send,
  Bot,
  Shield,
  LogOut,
  Database,
  User,
  BookOpen,
  Briefcase,
  FolderKanban,
  PieChart,
  UserPlus,
  FileStack
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, hasMinimumRole } = useAuth();
  const [showAIChat, setShowAIChat] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([
    {
      id: 1,
      type: 'ai',
      message: '안녕하세요! ProposalCraft AI 어시스턴트입니다. 공모사업 제안서 작성에 도움이 필요하시면 언제든 말씀해 주세요.',
      timestamp: new Date()
    }
  ]);

  const menuItems = [
    { path: '/', icon: Home, label: '대시보드' },
    { path: '/proposal-generator', icon: FileEdit, label: '사업계획서 작성', minRole: 'manager' as const },
    { path: '/grant-monitoring', icon: Radar, label: '공모사업 모니터링' },
    { path: '/proposals', icon: FolderOpen, label: '제안서 관리' },
    { path: '/company-pool', icon: Building2, label: '기업/기관 데이터풀' },
    { path: '/regional-knowhow', icon: Database, label: '지역 노하우', minRole: 'manager' as const },
    { path: '/matching', icon: Users2, label: '매칭 & 컨소시엄', minRole: 'manager' as const },
    { path: '/evaluation', icon: ClipboardCheck, label: '가상 평가' },
    { path: '/ppt-generator', icon: Presentation, label: 'PPT 생성', minRole: 'manager' as const },
    { path: '/portfolio', icon: Briefcase, label: '포트폴리오' },
    { path: '/projects', icon: FolderKanban, label: '프로젝트 관리' },
    { path: '/analytics', icon: PieChart, label: '분석 대시보드', minRole: 'manager' as const },
    { path: '/collaboration', icon: UserPlus, label: '협업', minRole: 'manager' as const },
    { path: '/templates', icon: FileStack, label: '문서 템플릿' },
    { path: '/settings', icon: Settings, label: '설정' },
    { path: '/demo-guide', icon: BookOpen, label: '사용 가이드' },
  ];

  // Admin 메뉴 (관리자만 표시)
  const adminMenuItems = hasMinimumRole('admin') ? [
    { path: '/admin', icon: Shield, label: '관리자 패널' },
  ] : [];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSendMessage = () => {
    if (chatMessage.trim()) {
      const userMessage = {
        id: Date.now(),
        type: 'user',
        message: chatMessage,
        timestamp: new Date()
      };
      
      setChatHistory(prev => [...prev, userMessage]);
      setChatMessage('');
      
      // AI 응답 시뮬레이션
      setTimeout(() => {
        const aiResponse = {
          id: Date.now() + 1,
          type: 'ai',
          message: '도움이 되었다니 기쁩니다! 더 구체적인 질문이 있으시면 언제든 말씀해 주세요.',
          timestamp: new Date()
        };
        setChatHistory(prev => [...prev, aiResponse]);
      }, 1000);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Rocket className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">ProposalCraft</h2>
              <p className="text-xs text-gray-500">AI 공모사업 플랫폼</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            // 권한 체크 (minRole이 있는 경우)
            if (item.minRole && !hasMinimumRole(item.minRole)) {
              return null;
            }
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
              >
                <Icon className="h-5 w-5 mr-3" />
                {item.label}
              </Link>
            );
          })}
          
          {/* Admin 메뉴 구분선 */}
          {adminMenuItems.length > 0 && (
            <>
              <div className="border-t border-gray-200 my-4"></div>
              {adminMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                  >
                    <Icon className="h-5 w-5 mr-3" />
                    {item.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* 사용자 프로필 & 로그아웃 */}
        {isAuthenticated && user && (
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                {user.profile_image_url ? (
                  <img src={user.profile_image_url} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  user.full_name?.charAt(0) || user.email.charAt(0).toUpperCase()
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.full_name || user.username}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4 mr-2" />
              로그아웃
            </button>
          </div>
        )}
        
        {/* 로그인 안 된 경우 */}
        {!isAuthenticated && (
          <div className="p-4 border-t border-gray-200">
            <Link
              to="/login"
              className="w-full flex items-center justify-center px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <User className="h-4 w-4 mr-2" />
              로그인
            </Link>
          </div>
        )}

        <div className="p-4 border-t border-gray-200">
          <div className="bg-emerald-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-emerald-900 mb-2">
              AI 어시스턴트
            </h3>
            <p className="text-xs text-emerald-700 mb-3">
              공모사업 제안서 작성에 도움이 필요하신가요?
            </p>
            <button 
              onClick={() => setShowAIChat(true)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
            >
              도움 요청
            </button>
          </div>
        </div>
      </aside>

      {/* AI 채팅 모달 */}
      {showAIChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-96 h-[600px] flex flex-col">
            {/* 헤더 */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <Bot className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">AI 어시스턴트</h3>
                  <p className="text-xs text-gray-500">온라인</p>
                </div>
              </div>
              <button
                onClick={() => setShowAIChat(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* 채팅 영역 */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {chatHistory.map((chat) => (
                <div
                  key={chat.id}
                  className={`flex ${chat.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs px-4 py-2 rounded-lg ${
                      chat.type === 'user'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm">{chat.message}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {chat.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* 입력 영역 */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="메시지를 입력하세요..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!chatMessage.trim()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar; 